import type { Signer } from '@ethersproject/abstract-signer'
import type { Provider } from '@ethersproject/providers'
import { Contract } from '@ethersproject/contracts'

import bep20Abi from 'config/abi/erc20.json'
import mproAbi from 'config/abi/mpro.json'
import nodeOwnersAbi from 'config/abi/nodeOwners.json'
import mproRewardStake from 'config/abi/mproRewardStake.json'
import uniswapRouterAbi from 'config/abi/uniswapRouter.json'
import uniswapPairAbi from 'config/abi/uniswapPair.json'
import mproStakeAbi from 'config/abi/mproStake.json'
import mproFeeVaultAbi from 'config/abi/mproFeeVault.json'
import { MproFeeVault } from 'config/abi/types/MproFeeVault'
import mproDoubleStakeAbi from 'config/abi/mproDoubleStake.json'
import mproRewardProxyAbi from 'config/abi/mproRewardProxy.json'
import mgaNodesAbi from 'config/abi/mgaNodes.json'
import mproDoubleRewardAutoStake from 'config/abi/mproDoubleRewardAutoStake.json'

import type {
  Erc20,
  Mpro,
  Multicall,
  NodeOwners,
  MproRewardStake,
  UniswapRouter,
  MproStake,
  MproRewardProxy,
  UniswapPair,
  MgaNodes,
  MproDoubleRewardAutoStake,
  MproDoubleStake
} from 'config/abi/types'
import multicallAbi from 'config/abi/multicall.json'
import { multicallContractAddress } from 'config/constants'
import { getSimpleRpcProviderByChainId, simpleRpcProvider } from './providers'

export const getContract = (abi: any, address = '', signer?: Signer | Provider, chainId?: number) => {
  const getSignerOrProvider = () => {
    if (signer) {
      return signer
    }
    if (chainId) {
      return getSimpleRpcProviderByChainId(chainId)
    }

    return simpleRpcProvider
  }

  return new Contract(address, abi, getSignerOrProvider())
}

export const getBep20Contract = (address: string, signer?: Signer | Provider | any, chainId?: number) => {
  return getContract(bep20Abi, address, signer, chainId) as Erc20
}

export const getMproContract = (address: string, signer?: Signer | Provider | any) => {
  return getContract(mproAbi, address, signer) as Mpro
}

export const getNodeOwnersContract = (contractAddress: string, signer?: Signer | Provider | any) => {
  return getContract(nodeOwnersAbi, contractAddress, signer) as NodeOwners
}

export const getMulticallContract = (chainId?: number) => {
  return getContract(multicallAbi, multicallContractAddress, undefined, chainId) as Multicall
}

export const getMproStakeRewardContract = (contractAddress: string, signer?: Signer | Provider | any) => {
  return getContract(mproRewardStake, contractAddress, signer) as MproRewardStake
}

export const getUniswapRouterContract = (
  contractAddress: string,
  signer?: Signer | Provider | any,
  chainId?: number
) => {
  return getContract(uniswapRouterAbi, contractAddress, signer, chainId) as UniswapRouter
}

export const getUniswapPairContract = (contractAddress: string, signer?: Signer | Provider | any, chainId?: number) => {
  return getContract(uniswapPairAbi, contractAddress, signer, chainId) as UniswapPair
}

/** Scope 14: the marketplace fee vault. Reads through a plain provider, writes through the wallet's signer. */
export const getFeeVaultContract = (contractAddress: string, signer?: Signer | Provider | any) => {
  return getContract(mproFeeVaultAbi, contractAddress, signer) as MproFeeVault
}

export const getClassicStakeContract = (contractAddress: string, signer?: Signer | Provider | any) => {
  return getContract(mproStakeAbi, contractAddress, signer) as MproStake
}

export const getClassicDoubleStakeContract = (contractAddress: string, signer?: Signer | Provider | any) => {
  return getContract(mproDoubleStakeAbi, contractAddress, signer) as MproDoubleStake
}

export const getMproRewardProxyContract = (contractAddress: string, signer?: Signer | Provider | any) => {
  return getContract(mproRewardProxyAbi, contractAddress, signer) as MproRewardProxy
}

export const getMgaRegularNodesContract = (contractAddress: string, signer?: Signer | Provider | any) => {
  return getContract(mgaNodesAbi, contractAddress, signer) as MgaNodes
}

export const getMgaLightNodesContract = (contractAddress: string, signer?: Signer | Provider | any) => {
  return getContract(mgaNodesAbi, contractAddress, signer) as MgaNodes
}

export const getDoubleAutoStakeContract = (contractAddress: string, signer?: Signer | Provider | any) => {
  return getContract(mproDoubleRewardAutoStake, contractAddress, signer) as MproDoubleRewardAutoStake
}
